import { keepPreviousData } from "@tanstack/react-query";
import isNil from "lodash/isNil";
import { useActivityShortlists } from "PFApp/activities/show/hooks/use_activity_shortlists";
import { useCurrentActivity } from "PFApp/activities/show/hooks/use_current_activity";
import { RoleWatcher } from "PFApp/activities/types/watchers";
import { useApiFilters, useFiltersV2MetaAutoUpdate, useFiltersV2Select } from "PFApp/components/filters_v2";
import { useStorageTableColumns } from "PFApp/hooks/use_storage_table_columns";
import useStorageOrder from "PFApp/use_filtered_collection/use_storage_order";
import { getProfileName } from "PFCore/helpers/profile";
import useStorage from "PFCore/helpers/use_storage";
import { useInfiniteActivityWatchers } from "PFCore/hooks/queries/activity/use_activity_watchers";
import { useDateFormatter } from "PFCore/hooks/use_date_formatter";
import { convertToPercentage } from "PFCore/utilities/math";
import { PropsWithChildren, useCallback, useEffect, useMemo, useState } from "react";

import { InterestedContext } from "./interested_context";

const PER_PAGE = 15;
import { useTranslation } from "react-i18next";

import availableColumns from "../constants/columns";
import useCustomInterestColumns from "../hooks/use_custom_interest_columns";
import { useExcludeHiddenColumns } from "../hooks/use_exclude_hidden_columns";
import { useTermFilteredItems } from "../hooks/use_term_filtered_items";

type InterestedContextProviderProps = PropsWithChildren<{
  onInterestsCountChange: (count: number) => void;
}>;

export const InterestedContextProvider = ({
  onInterestsCountChange,
  children
}: InterestedContextProviderProps) => {
  const activity = useCurrentActivity();
  const { shortlists } = useActivityShortlists();
  const { formatDate } = useDateFormatter();
  const [selectedIds, setSelectedIds] = useState({});
  const [showTableProperties, setTablePropertiesVisibility] = useState(false);
  const excludeHiddenColumns = useExcludeHiddenColumns();
  const { t } = useTranslation("activities");

  const { customColumns } = useCustomInterestColumns();
  const columns = useMemo(() => availableColumns.concat(customColumns), [customColumns]);
  const { selectedColumns, setSelectedColumns } = useStorageTableColumns(
    `activity_interested_columns_v2`,
    columns,
    availableColumns.filter(({ isDefault }) => isDefault)
  );

  const { selectedOrder, setSelectedOrder } = useStorageOrder({ storagePrefix: "activity_interested" });

  const [searchTerm, setSearchTerm] = useStorage<string>("activity_interested_search_term", "");

  const { apiFilters, areApiFiltersReady } = useApiFilters();
  const { watchers, isLoading, data } = useInfiniteActivityWatchers(
    activity,
    PER_PAGE,
    apiFilters,
    selectedOrder,
    {
      placeholderData: keepPreviousData,
      enabled: areApiFiltersReady
    }
  );

  const watchersMetadata = data?.pages?.[0].meta;
  const interestsCount = data?.pages?.[0].meta.total;
  const { isSidePanelOpen: areFiltersOpen } = useFiltersV2Select("isSidePanelOpen");

  useFiltersV2MetaAutoUpdate(watchersMetadata);

  useEffect(() => {
    if (!isNil(interestsCount)) {
      onInterestsCountChange(interestsCount);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [interestsCount]);

  useEffect(() => {
    setTablePropertiesVisibility(false);
  }, [areFiltersOpen]);

  const formatShortlist = useCallback(
    (profile) => {
      if (!profile) {
        return "";
      }
      const isShortlisted = !!(shortlists || []).find(
        (shortlist) => shortlist.profile && shortlist.profile.id === profile.id
      );
      return isShortlisted ? t("show.parts.shortlisted") : t("show.parts.shortlist");
    },
    [shortlists, t]
  );

  const filterOptions = useMemo(
    () => ({
      selectedColumns,
      formatValues: {
        createdAt: formatDate,
        name: getProfileName,
        normalizedScore: (rawValue) => convertToPercentage(rawValue?.normalizedScore),
        availabilityScore: (rawValue) => convertToPercentage(rawValue?.availabilityScore),
        shortlist: formatShortlist
      }
    }),
    [formatDate, formatShortlist, selectedColumns]
  );

  const { filteredItems: filteredWatchers } = useTermFilteredItems(
    watchers,
    String(searchTerm || ""),
    filterOptions
  );

  const contextValue = useMemo(
    () => ({
      selectedIds,
      setSelectedIds,
      showTableProperties,
      setTablePropertiesVisibility,
      columns: excludeHiddenColumns(columns),
      selectedColumns: excludeHiddenColumns(selectedColumns),
      setSelectedColumns,
      isWatchersLoading: isLoading,
      watchersMetadata,
      watchers: watchers as RoleWatcher[],
      filteredWatchers: filteredWatchers as RoleWatcher[],
      setSelectedOrder,
      searchTerm,
      setSearchTerm
    }),
    [
      selectedIds,
      showTableProperties,
      columns,
      selectedColumns,
      excludeHiddenColumns,
      setSelectedColumns,
      isLoading,
      watchersMetadata,
      watchers,
      filteredWatchers,
      setSelectedOrder,
      searchTerm,
      setSearchTerm
    ]
  );

  return <InterestedContext.Provider value={contextValue}>{children}</InterestedContext.Provider>;
};
